import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Paper,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const ring = keyframes`
  0%, 100% {
    transform: rotate(0deg);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: rotate(-10deg);
  }
  20%, 40%, 60%, 80% {
    transform: rotate(10deg);
  }
`;

/**
 * IncomingCallModal Component
 * Shows when a user receives an incoming video/audio call notification
 */
const IncomingCallModal = ({ 
  open, 
  callerName, 
  callType, 
  callUrl, 
  onAccept, 
  onDecline,
  onClose 
}) => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [ringing, setRinging] = useState(true);

  // Play ringing sound using Web Audio API
  useEffect(() => {
    if (open && ringing) {
      let audioContext = null;
      let ringInterval = null;

      try {
        // Create audio context
        audioContext = new (window.AudioContext || window.webkitAudioContext)();

        // Play beep pattern function
        const playBeep = () => {
          if (!ringing || !audioContext) return;
          
          try {
            if (audioContext.state === 'suspended') {
              audioContext.resume();
            }

            // Create new oscillator for each beep
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            // Configure beep sound
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);

            // Connect nodes
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            // Play beep for 0.2 seconds
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.2);
          } catch (error) {
            // Ignore errors for individual beeps
            console.log('Beep error:', error);
          }
        };

        // Play beep every 2 seconds
        ringInterval = setInterval(playBeep, 2000);
        playBeep(); // Play immediately

        return () => {
          if (ringInterval) {
            clearInterval(ringInterval);
          }
          if (audioContext) {
            audioContext.close().catch(() => {
              // Ignore errors when closing
            });
          }
        };
      } catch (error) {
        // Silently fail if Web Audio API is not available
        console.log('Audio not available:', error);
      }
    }
  }, [open, ringing]);

  const handleAccept = () => {
    setRinging(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    // Validate callUrl before navigating
    if (!callUrl || callUrl === '/video-call' || !callUrl.includes('appointmentId=')) {
      console.error('[IncomingCallModal] Invalid callUrl:', callUrl);
      alert('Cannot join call: Invalid call link. Please ask the caller to start a new call.');
      if (onClose) {
        onClose();
      }
      return;
    }
    
    // Don't refresh session here - AuthContext handles proactive refresh
    // Refreshing here can cause 500 errors from Supabase that trigger SIGNED_OUT
    // Just navigate - the token should still be valid (refreshed proactively)
    
    if (onAccept) {
      onAccept();
    }
    
    // Navigate to the call URL (should include appointmentId parameter)
    navigate(callUrl);
    
    if (onClose) {
      onClose();
    }
  };

  const handleDecline = useCallback(() => {
    setRinging(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (onDecline) {
      onDecline();
    }
    if (onClose) {
      onClose();
    }
  }, [onDecline, onClose]);

  // Auto-decline after 30 seconds if not answered
  useEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        handleDecline();
      }, 30000); // 30 seconds

      return () => clearTimeout(timeout);
    }
  }, [open, handleDecline]);

  return (
    <>
      {/* Hidden audio element for ringing */}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZkDcIG2m98OScTgwOUKzn8LZjHAY4kdfyzHksBSR3x/DdkEAKFF606euoVRQKRp/g8r5sIQUrgc7y2ZA3CBtpvfDknE4MDlCs5/C2YxwGOJHX8sx5LAUkd8fw3ZBAC" type="audio/wav" />
      </audio>

      <Dialog
        open={open}
        onClose={handleDecline}
        maxWidth="sm"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 }, // Full width on mobile
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            m: { xs: 0, sm: 2 }, // No margin on mobile for full-width
            maxWidth: { xs: '100%', sm: '400px' },
          }
        }}
      >
        <DialogContent sx={{ 
          p: { xs: 3, sm: 4 }, 
          textAlign: 'center',
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 3, sm: 4 },
          }
        }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: { xs: 2, sm: 3 },
            }}
          >
            {/* Caller Avatar with Animation */}
            <Box
              sx={{
                position: 'relative',
                animation: ringing ? `${pulse} 2s ease-in-out infinite` : 'none',
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  fontSize: { xs: 40, sm: 48 },
                  border: '4px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                {callerName?.charAt(0).toUpperCase() || '?'}
              </Avatar>
              {ringing && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 120, sm: 140 },
                    height: { xs: 120, sm: 140 },
                    border: '3px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '50%',
                    animation: `${pulse} 2s ease-in-out infinite`,
                  }}
                />
              )}
            </Box>

            {/* Call Type Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                animation: ringing ? `${ring} 0.5s ease-in-out infinite` : 'none',
              }}
            >
              {callType === 'video' ? (
                <VideocamIcon sx={{ fontSize: 32 }} />
              ) : (
                <PhoneIcon sx={{ fontSize: 32 }} />
              )}
            </Box>

            {/* Caller Name */}
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              {callerName || 'Incoming Call'}
            </Typography>

            {/* Call Type */}
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9, 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              {callType === 'video' ? 'Video Call' : 'Audio Call'}
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1.5, sm: 2 }, 
              mt: 2,
              width: '100%',
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
            }}>
              {/* Accept Button */}
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={handleAccept}
                startIcon={<PhoneIcon />}
                sx={{
                  minWidth: { xs: '100%', sm: 140 },
                  py: { xs: 1.75, sm: 1.5 },
                  borderRadius: 3,
                  bgcolor: 'rgba(76, 175, 80, 0.9)',
                  fontSize: { xs: '1rem', sm: '0.875rem' },
                  minHeight: { xs: 48, sm: 40 }, // Touch-friendly on mobile
                  '&:hover': {
                    bgcolor: 'rgba(76, 175, 80, 1)',
                  },
                }}
              >
                Accept
              </Button>

              {/* Decline Button */}
              <Button
                variant="contained"
                color="error"
                size="large"
                onClick={handleDecline}
                startIcon={<CallEndIcon />}
                sx={{
                  minWidth: { xs: '100%', sm: 140 },
                  py: { xs: 1.75, sm: 1.5 },
                  borderRadius: 3,
                  bgcolor: 'rgba(244, 67, 54, 0.9)',
                  fontSize: { xs: '1rem', sm: '0.875rem' },
                  minHeight: { xs: 48, sm: 40 }, // Touch-friendly on mobile
                  '&:hover': {
                    bgcolor: 'rgba(244, 67, 54, 1)',
                  },
                }}
              >
                Decline
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IncomingCallModal;

